import { CHOICES_FRAGMENT } from '../../../apollo/questions.ts';
import { useFragment } from '../../../apollo/__generated__';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import Markdown from '../../markdown.tsx';
import Button from '../../common/button.tsx';
import QuestionAnswer from './question-answer.tsx';
import QuestionToolbar, { IQuestionInfoData } from './question-toolbar.tsx';
import MarkButton from './mark-button.tsx';
import { cn, normalizeCollapsedQuestionText, orderToLetter } from '../../../utils/common-utils.ts';
import {
  Maybe,
  QuestionAnswerType,
  QuestionFragmentFragment,
} from '../../../apollo/__generated__/graphql.ts';
import { MathContent } from './math-content.tsx';
import { isEqual, uniq } from 'lodash';
import Watermark from '../../watermark';
import { useIsDesktop } from '../../../utils/hooks.ts';
import HighlightedText from '../../hightlight/index.tsx';
import { HighlightContentTypeEnum } from '../../../enums/index.ts';
import HighlightStickersWrapper from '../../hightlight/highlight-stickers-wrapper/index.tsx';
import DesmosCalculator from './desmos-calculator.tsx';

export interface ICompletedData {
  userAnswer?: string | number | null;
  correctAnswer?: string | number | null;
  isCorrect?: boolean | null;
}

interface IChoicePreview {
  type?: Maybe<QuestionAnswerType>;
  choices: string[];
}

interface IProps {
  highlightStickersToggle?: boolean;
  isShowHighLights?: boolean;
  prompt: string;
  content?: string | null;
  explanation?: string | null;
  choicesData?: QuestionFragmentFragment['choices'];
  excludedChoices?: number[] | null;
  isMath: boolean;
  isMark?: boolean;
  answer?: string | number;
  choicesPreview?: IChoicePreview; // for preview questions in edit/create questions mode
  setMark?: (mark: boolean) => void;
  setExcludedChoices?: (excludedChoices?: number[]) => void;
  sendAnswer?: () => void;
  isPreviewMode?: boolean;
  setAnswer?: Dispatch<SetStateAction<string | number | undefined>>;
  isReportAvailable?: boolean;
  questionInfoData?: IQuestionInfoData[];
  completedData?: ICompletedData;
  questionId?: number;
  questionNumber?: number;
  isModalMode?: boolean;
  isExplanation?: boolean;
  setIsExplanation?: (value: boolean) => void;
  isAnswerVisible?: boolean;
  setIsAnswerVisible?: (value: boolean) => void;
  isCalc?: boolean;
  setIsCalc?: Dispatch<SetStateAction<boolean>>;
  stickyBottomActions?: boolean;
  allowReviewInteractions?: boolean;
  hasQuestionStickers?: boolean;
  onToggleNoteVisibility?: () => void;
  reviewStartsRevealed?: boolean;
}

const NoteRailChevron = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg
    viewBox="0 0 12 12"
    aria-hidden="true"
    className="h-[14px] w-[14px]"
    fill="none"
  >
    <path
      d={
        direction === 'right'
          ? 'M4 2.5L7.75 6L4 9.5'
          : 'M8 2.5L4.25 6L8 9.5'
      }
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const QuestionContent = ({
  prompt,
  content,
  choicesData,
  explanation,
  setMark,
  setExcludedChoices,
  excludedChoices,
  isMark,
  sendAnswer,
  isPreviewMode = false,
  setAnswer,
  answer,
  choicesPreview,
  questionInfoData,
  isReportAvailable = false,
  isMath,
  completedData,
  questionId,
  questionNumber,
  isModalMode = true,
  isExplanation,
  setIsExplanation,
  isAnswerVisible = false,
  setIsAnswerVisible,
  highlightStickersToggle,
  isShowHighLights,
  isCalc: controlledIsCalc,
  setIsCalc: setControlledIsCalc,
  stickyBottomActions = false,
  allowReviewInteractions = false,
  hasQuestionStickers = false,
  onToggleNoteVisibility,
  reviewStartsRevealed = false,
}: IProps) => {
  const { isDesktop } = useIsDesktop();
  const choices = useFragment(CHOICES_FRAGMENT, choicesData);
  const [isCrossOutMode, setIsCrossOutMode] = useState<boolean>(
    window?.localStorage
      ? !!window.localStorage?.getItem('isCrossOutMode')
      : false,
  );
  const [excluded, setExcluded] = useState<number[]>(excludedChoices ?? []);
  const [reviewAnswer, setReviewAnswer] = useState<string | number | undefined>();
  const [reviewSubmittedData, setReviewSubmittedData] =
    useState<ICompletedData>();
  const [internalIsCalc, setInternalIsCalc] = useState(false);
  const isCalc = controlledIsCalc ?? internalIsCalc;
  const setIsCalc = setControlledIsCalc ?? setInternalIsCalc;
  const isReviewAttemptMode = allowReviewInteractions && isPreviewMode;
  const reviewStartsRevealedRef = useRef(reviewStartsRevealed);
  const answerVisibleSetterRef = useRef(setIsAnswerVisible);
  const explanationSetterRef = useRef(setIsExplanation);

  reviewStartsRevealedRef.current = reviewStartsRevealed;
  answerVisibleSetterRef.current = setIsAnswerVisible;
  explanationSetterRef.current = setIsExplanation;

  useEffect(() => {
    const nextExcludedChoices = [...(excludedChoices ?? [])].sort();
    setExcluded((currentExcludedChoices) => {
      const sortedCurrentExcludedChoices = [...currentExcludedChoices].sort();

      if (isEqual(nextExcludedChoices, sortedCurrentExcludedChoices)) {
        return currentExcludedChoices;
      }

      return excludedChoices ?? [];
    });
  }, [excludedChoices]);

  useEffect(() => {
    if (!setExcludedChoices) {
      setExcluded(excludedChoices ?? []);
    }
  }, [excludedChoices, questionId, setExcludedChoices]);

  useEffect(() => {
    if (!isReviewAttemptMode) {
      return;
    }

    const shouldStartRevealed =
      !!completedData && !!reviewStartsRevealedRef.current;

    setReviewAnswer(undefined);
    setReviewSubmittedData(shouldStartRevealed ? completedData : undefined);
    setExcluded([]);
    answerVisibleSetterRef.current?.(shouldStartRevealed);
    explanationSetterRef.current?.(false);
  }, [
    completedData,
    isReviewAttemptMode,
    questionId,
  ]);

  useEffect(() => {
    if (
      !isReviewAttemptMode ||
      !isAnswerVisible ||
      !completedData ||
      reviewSubmittedData
    ) {
      return;
    }

    setReviewSubmittedData(completedData);
  }, [
    completedData,
    isAnswerVisible,
    isReviewAttemptMode,
    reviewSubmittedData,
  ]);

  const toggleCrossOutMode = () => {
    setIsCrossOutMode((prevState) => {
      const mode = !prevState;
      if (window?.localStorage) {
        if (mode) window.localStorage.setItem('isCrossOutMode', 'true');
        else window.localStorage.removeItem('isCrossOutMode');
      }
      return !prevState;
    });
  };

  const onSetExcludedChoices = (isCrossed: boolean, choiceId: number) => {
    const updatedExcluded = isCrossed
      ? uniq([...excluded, choiceId])
      : excluded.filter((id) => id !== choiceId);
    setExcluded(updatedExcluded);
    setExcludedChoices?.(updatedExcluded);
  };

  const toggleExplanation = () => setIsExplanation?.(!isExplanation);

  const correctChoiceId = choices?.find((choice) => choice.correct)?.id;
  const resolvedCorrectAnswer =
    correctChoiceId != null ? correctChoiceId : completedData?.correctAnswer;
  const currentAnswer = isReviewAttemptMode ? reviewAnswer : answer;
  const isReviewSubmitted = !!reviewSubmittedData;
  const canSubmitReviewAnswer =
    isReviewAttemptMode &&
    !isReviewSubmitted &&
    currentAnswer != null &&
    String(currentAnswer).trim().length > 0;
  const canShowReviewAnswer = !!completedData;
  const visibleCompletedData = isAnswerVisible
    ? isReviewAttemptMode
      ? reviewSubmittedData ?? completedData
      : completedData
    : undefined;
  const effectiveExcluded =
    isAnswerVisible && correctChoiceId != null
      ? excluded.filter((choiceId) => choiceId !== correctChoiceId)
      : excluded;

  const getCompletedStatusLabel = (
    data?: ICompletedData,
  ): 'Correct' | 'Incorrect' | 'Omitted' | null => {
    if (!data) {
      return null;
    }

    if (data.isCorrect === true) {
      return 'Correct';
    }

    if (data.userAnswer == null || String(data.userAnswer).trim() === '') {
      return 'Omitted';
    }

    return 'Incorrect';
  };

  const reviewOutcomeLabel = isReviewAttemptMode
    ? getCompletedStatusLabel(reviewSubmittedData)
    : null;
  const visibleReviewOutcomeLabel =
    isReviewAttemptMode &&
    (reviewSubmittedData?.isCorrect === false || isAnswerVisible)
      ? reviewOutcomeLabel
      : null;

  const onSubmitReviewAnswer = () => {
    if (!canSubmitReviewAnswer) {
      return;
    }

    const normalizeAnswerValue = (value?: string | number | null) =>
      normalizeCollapsedQuestionText(String(value ?? ''))
        .replace(/\s+/g, '')
        .trim()
        .toLowerCase();

    const isCorrect =
      choices?.length && resolvedCorrectAnswer != null
        ? Number(currentAnswer) === Number(resolvedCorrectAnswer)
        : normalizeAnswerValue(currentAnswer).length > 0 &&
          normalizeAnswerValue(currentAnswer) ===
            normalizeAnswerValue(resolvedCorrectAnswer);

    setReviewSubmittedData({
      userAnswer: currentAnswer,
      correctAnswer: resolvedCorrectAnswer,
      isCorrect,
    });
    setIsAnswerVisible?.(isCorrect);

    if (isCorrect) {
      setIsExplanation?.(false);
    }
  };

  const onTryReviewAgain = () => {
    setReviewAnswer(undefined);
    setReviewSubmittedData(undefined);
    setExcluded([]);
    setIsAnswerVisible?.(false);
    setIsExplanation?.(false);
  };

  const onChangeAnswer = (value?: string | number) => {
    if (isReviewAttemptMode) {
      setReviewAnswer(value);
      return;
    }

    if (setAnswer) setAnswer(value);
  };
  const hasReviewToggleButtons =
    !!completedData || (!!explanation && isPreviewMode);

  const formatCompletedAnswerValue = (
    value?: string | number | null,
  ): string | null => {
    if (value == null) {
      return null;
    }

    if (choices?.length) {
      const choice = choices.find((currentChoice) => currentChoice.id === Number(value));

      if (choice) {
        return orderToLetter(choice.order);
      }
    }

    return normalizeCollapsedQuestionText(String(value));
  };

  const renderCompletedAnswerSummary = () => {
    if (!visibleCompletedData) {
      return null;
    }

    const userAnswer = formatCompletedAnswerValue(visibleCompletedData.userAnswer);
    const correctAnswer = formatCompletedAnswerValue(visibleCompletedData.correctAnswer);
    const completedStatusLabel = getCompletedStatusLabel(visibleCompletedData);

    return (
      <div className="mt-6 rounded-xl border bg-light-gray p-4 text-sm leading-relaxed">
        {completedStatusLabel === 'Correct' ? (
          <div className="font-semibold text-green">Correct</div>
        ) : completedStatusLabel === 'Omitted' ? (
          <div className="font-semibold text-gray">Omitted</div>
        ) : (
          <div className="font-semibold text-red">Incorrect</div>
        )}

        {visibleCompletedData.isCorrect === false && !!userAnswer && (
          <div className="mt-3 flex flex-wrap items-start gap-2">
            <span className="font-semibold">Your answer:</span>
            <Markdown>{userAnswer}</Markdown>
          </div>
        )}

        {!!correctAnswer && (
          <div className="mt-3 flex flex-wrap items-start gap-2">
            <span className="font-semibold">Correct answer:</span>
            <Markdown>{correctAnswer}</Markdown>
          </div>
        )}
      </div>
    );
  };

  const renderExplanationPanel = () => {
    if (!(explanation && isPreviewMode && isExplanation)) {
      return null;
    }

    return (
      <div className="mt-6 rounded-xl border bg-light-gray p-4 text-sm leading-relaxed">
        <Markdown>{explanation}</Markdown>
      </div>
    );
  };

  const renderReviewToggleButtons = (className: string) => {
    if (!hasReviewToggleButtons) {
      return null;
    }

    const primaryReviewButtonClass =
      '!rounded-full !border !border-blue !bg-blue !px-4 !py-2 !text-sm !font-semibold !text-white transition-colors hover:!bg-dark-blue disabled:!border-[#E2E8F0] disabled:!bg-[#ECEFF3] disabled:!text-[#98A2B3] disabled:hover:!bg-[#ECEFF3]';
    const secondaryReviewButtonClass =
      '!rounded-full !border !border-[#D7E3F2] !bg-light-gray !px-4 !py-2 !text-sm !font-semibold !text-dark-blue transition-colors hover:!bg-[#E8EDF3] disabled:!border-[#E2E8F0] disabled:!bg-[#ECEFF3] disabled:!text-[#98A2B3] disabled:hover:!bg-[#ECEFF3]';

    return (
      <div className={cn(className, 'flex-col')}>
        {visibleReviewOutcomeLabel && (
          <div
            className={cn(
              'w-full text-center text-sm font-semibold',
              visibleReviewOutcomeLabel === 'Correct'
                ? 'text-green'
                : visibleReviewOutcomeLabel === 'Omitted'
                ? 'text-gray'
                : 'text-red',
            )}
          >
            {visibleReviewOutcomeLabel}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          {isReviewAttemptMode && (
            <Button
              white
              className={primaryReviewButtonClass}
              onClick={isReviewSubmitted ? onTryReviewAgain : onSubmitReviewAnswer}
              disabled={!isReviewSubmitted && !canSubmitReviewAnswer}
            >
              {isReviewSubmitted ? 'Retry' : 'Submit Answer'}
            </Button>
          )}

          {completedData && (
            <Button
              white
              className={secondaryReviewButtonClass}
              onClick={() => setIsAnswerVisible?.(!isAnswerVisible)}
              disabled={!canShowReviewAnswer}
            >
              {isAnswerVisible ? 'Hide Answer' : 'Show Answer'}
            </Button>
          )}

          {explanation && isPreviewMode && (
            <Button
              white
              className={secondaryReviewButtonClass}
              onClick={toggleExplanation}
              disabled={isReviewAttemptMode && !isAnswerVisible}
            >
              {isExplanation ? 'Hide explanation' : 'Show explanation'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const isShowLeftSideQuestion = (() => {
    if (isMath && !choicesData?.length) return true;

    const trimedContent = content?.trim();

    if (trimedContent === `<span class="text-center"></span>`) return false;

    return trimedContent;
  })();

  const isNeedMoveRightSideForCalc =
    !isShowLeftSideQuestion && isCalc && isDesktop;

  const canShowHighlightsAndNotes = !!isShowHighLights && !isMath;
  const isShowHighLightStickrs =
    canShowHighlightsAndNotes &&
    !!highlightStickersToggle &&
    hasQuestionStickers;
  const isShowNotesHandle =
    canShowHighlightsAndNotes &&
    hasQuestionStickers &&
    !!onToggleNoteVisibility;

  function setVhUnit() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  setVhUnit();
  window.addEventListener('resize', setVhUnit);
  const questionHeader = (
    <div className={'w-full bg-white pb-1'}>
      <QuestionToolbar
        data={questionInfoData}
        isReportAvailable={isReportAvailable}
        isMath={isMath}
        questionId={questionId}
        setIsCalc={setIsCalc}
      />

      <div
        className={cn(
          'pb-[2px]',
        )}
      >
        <div
          className={cn(
            'flex min-h-8 items-center justify-between rounded-none bg-light-gray',
          )}
        >
          <div className="flex min-w-0 items-center gap-[1.23rem] py-1 text-sm">
            {!!questionNumber && (
              <span
                className="flex h-9 min-w-[35px] shrink-0 items-center justify-center bg-black px-1 text-[1.125rem] font-semibold leading-none text-white"
              >
                {questionNumber}
              </span>
            )}
            {setMark && (!isPreviewMode || allowReviewInteractions) && (
              <MarkButton
                isMark={!!isMark}
                toggleMark={() => setMark(!isMark)}
              />
            )}
          </div>
          <div className={'flex items-center py-1 text-sm'}>
            {choicesData?.length && (!isPreviewMode || allowReviewInteractions) && (
              <button
                disabled={false}
                className={cn(
                        'relative mx-2 mr-2 h-6 w-[1.72rem] rounded border border-gray px-0 py-0.5 text-center text-xs font-bold',
                  isCrossOutMode
                    ? 'bg-blue text-white'
                    : 'bg-white text-black',
                )}
                onClick={toggleCrossOutMode}
                    >
                      <div className="absolute left-1/2 top-1/2 h-px w-3/4 -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded bg-gray ring-1 ring-white" />
                      <span className="inline-block scale-x-[0.85]">ABC</span>
                    </button>
                  )}
                </div>
        </div>
        <div className="mt-[2px] h-px w-full bg-[repeating-linear-gradient(to_right,#000_0_18px,transparent_18px_20px)]" />
      </div>
    </div>
  );
  return (
    <div
      className={cn(
        'relative flex w-full flex-col',
        stickyBottomActions && 'h-full min-h-0',
      )}
    >
      <div
        className={cn(
          'flex w-full justify-center',
          stickyBottomActions
            ? 'min-h-0 flex-1'
            : explanation && completedData
            ? 'h-[calc(var(--vh,1vh)*100-11rem)]'
            : 'h-[calc(var(--vh,1vh)*100-8rem)]',
        )}
      >
        {!isDesktop && <Watermark />}

        {isShowLeftSideQuestion && (
          <div
            className={cn(
              `relative h-full w-2/4 overflow-y-auto overflow-x-hidden border-r-2 border-[#C9CDD4] p-4 pt-9 text-[90%]`,
            )}
          >
            <div className="pointer-events-none sticky top-60 z-[200] select-none">
              {isDesktop && <Watermark />}
            </div>

            <div className="font-gentium text-base [&>*:first-child]:mt-0 [&_p:first-child]:mt-0 [&_div:first-child]:mt-0">
              {content && canShowHighlightsAndNotes ? (
                <HighlightedText
                  key={`${questionId}_${HighlightContentTypeEnum.CONTENT}`}
                  value={content}
                  questionId={questionId!}
                  highlightParentKey={`${questionId}_${HighlightContentTypeEnum.CONTENT}`}
                />
              ) : (
                content && <Markdown>{content}</Markdown>
              )}
            </div>

            {isShowLeftSideQuestion && isMath && !choicesData?.length && (
              <MathContent />
            )}
          </div>
        )}

        {isShowNotesHandle ? (
          <div
            className={cn(
              'relative shrink-0 overflow-visible border-r-2 border-[#C9CDD4] bg-[#EEF1F5] transition-[width,padding] duration-300',
              isShowHighLightStickrs
                ? 'w-60 pl-2 pr-2 pt-12'
                : 'w-[18px] px-0 pt-0',
            )}
          >
            {isShowHighLightStickrs && (
              <HighlightStickersWrapper
                questionId={questionId!}
                className={'w-full border-r-0 px-0 pt-0'}
              />
            )}

            <button
              type="button"
              className={cn(
                'absolute bottom-4 z-[80] flex h-[30px] w-[24px] items-center justify-center border-2 border-[#C9CDD4] bg-[#676767] text-white shadow-md transition-colors hover:bg-[#575757]',
                isShowHighLightStickrs
                  ? 'left-0 rounded-r-full border-l-0'
                  : 'left-[-26px] rounded-l-full border-r-0',
              )}
              onClick={onToggleNoteVisibility}
              aria-label={isShowHighLightStickrs ? 'Hide notes' : 'Show notes'}
            >
              <NoteRailChevron
                direction={isShowHighLightStickrs ? 'right' : 'left'}
              />
            </button>
          </div>
        ) : (
          isShowHighLightStickrs && (
            <HighlightStickersWrapper
              questionId={questionId!}
              className={'w-60'}
            />
          )
        )}

        <div
          className={cn(
            stickyBottomActions
              ? 'relative flex h-full min-h-0 w-2/4 flex-col overflow-hidden px-4 pb-4 pt-9'
              : 'relative h-full w-2/4 overflow-y-auto overflow-x-hidden px-4 pb-4 pt-9',
            !isShowLeftSideQuestion && !isCalc && 'w-full max-w-208',
          )}
          style={{
            transition: 'margin-left 0.5s ease',
            marginLeft: isNeedMoveRightSideForCalc ? '50%' : '0%',
          }}
        >
          {!stickyBottomActions && questionHeader}

          <div
            className={cn(
              stickyBottomActions
                ? 'relative flex min-h-0 flex-1 flex-col pt-0'
                : 'relative pt-0',
              isModalMode &&
                !stickyBottomActions &&
                'max-h-[calc(var(--vh,1vh)*100-14rem)] min-h-[calc(var(--vh,1vh)*100-12rem)]',
            )}
          >
            <div
              className={cn(
                stickyBottomActions
                  ? 'min-h-0 flex-1 overflow-y-auto pr-2'
                  : undefined,
                'text-[90%]',
              )}
            >
              {stickyBottomActions && questionHeader}

              <div className="pointer-events-none sticky top-60 z-[200] select-none">
                {isDesktop && <Watermark />}
              </div>

              <div className="font-gentium text-base pt-2 pb-3 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                {canShowHighlightsAndNotes ? (
                  <HighlightedText
                    key={`${questionId}_${HighlightContentTypeEnum.PROMPT}`}
                    value={prompt}
                    questionId={questionId!}
                    highlightParentKey={`${questionId}_${HighlightContentTypeEnum.PROMPT}`}
                  />
                ) : (
                  <Markdown>{prompt}</Markdown>
                )}
              </div>

              <div className={'flex flex-col gap-2 overflow-x-hidden pr-2'}>
                {!choicesPreview ? (
                  choicesData?.length ? (
                    choices?.map((choice) => {
                      return (
                        <QuestionAnswer
                          key={choice.id}
                          order={choice.order}
                          text={choice.text || ''}
                          value={choice.id}
                          excluded={
                            effectiveExcluded?.includes(Number(choice.id)) ??
                            false
                          }
                          onChange={onChangeAnswer}
                          isDisabled={
                            isReviewAttemptMode
                              ? isReviewSubmitted
                              : !!completedData || isPreviewMode
                          }
                          isChecked={Number(currentAnswer) === choice.id}
                          isCrossOutMode={isCrossOutMode}
                          onSetExcludedChoices={onSetExcludedChoices}
                          isRightAnswerMark={
                            visibleCompletedData?.isCorrect != null &&
                            (visibleCompletedData?.correctAnswer === choice.id ||
                              !!choice.correct)
                          }
                          isWrongAnswerMark={
                            visibleCompletedData?.isCorrect != null &&
                            !visibleCompletedData?.isCorrect &&
                            visibleCompletedData?.userAnswer === choice.id
                          }
                          isPreviewMode={isPreviewMode}
                          questionId={questionId}
                          isShowHighLights={canShowHighlightsAndNotes}
                        />
                      );
                    })
                  ) : (
                    <input
                      className={'base-input'}
                      placeholder={'Enter your answer...'}
                      value={currentAnswer ?? ''}
                      onChange={(e) => onChangeAnswer(e.target.value)}
                      disabled={
                        isReviewAttemptMode
                          ? isReviewSubmitted
                          : !!completedData || isPreviewMode
                      }
                      maxLength={String(currentAnswer)?.startsWith('-') ? 6 : 5}
                    />
                  )
                ) : choicesPreview?.type === QuestionAnswerType.MultipleChoice ? (
                  <div className={'mt-2 space-y-2'}>
                    {choicesPreview?.choices.map((item, index) => {
                      return (
                        <QuestionAnswer
                          key={index}
                          order={index}
                          text={item}
                          value={index}
                          isChecked={false}
                          isCrossOutMode={isCrossOutMode}
                          isPreviewMode={isPreviewMode}
                        />
                      );
                    })}
                  </div>
                ) : (
                  choicesPreview?.choices.map((item, index) => {
                    return (
                      <input
                        key={index}
                        className={'base-input'}
                        value={item ?? ''}
                        disabled={true}
                      />
                    );
                  })
                )}
              </div>

              {!choices &&
                !!currentAnswer?.toString()?.length &&
                !isPreviewMode &&
                !completedData && (
                  <div
                    className={
                      'mt-10 flex items-center font-gentium text-xl font-bold'
                    }
                  >
                    <div className="mr-2">Answer Preview: </div>
                    <Markdown>{`$${currentAnswer?.toString()}$`}</Markdown>
                  </div>
                )}

              {renderCompletedAnswerSummary()}
              {stickyBottomActions && renderExplanationPanel()}
            </div>

            {stickyBottomActions &&
              renderReviewToggleButtons(
                'mt-3 flex shrink-0 flex-wrap justify-center gap-2 border-t border-table-border bg-white/95 pt-3 backdrop-blur',
              )}
          </div>
        </div>
      </div>

      {!stickyBottomActions && hasReviewToggleButtons && (
        <div className="flex flex-col">
          {renderReviewToggleButtons('my-4 flex items-center justify-center gap-2')}
          {explanation && isExplanation && (
            <div className="m-4 flex-col rounded-xl border bg-light-gray p-4 font-gentium text-sm leading-relaxed">
              <Markdown>{explanation!}</Markdown>
            </div>
          )}
        </div>
      )}

      {sendAnswer && (
        <div className={'mt-10 flex justify-end'}>
          <Button
            className={'w-32'}
            disabled={!!completedData}
            onClick={sendAnswer}
          >
            Check answer
          </Button>
        </div>
      )}

      <DesmosCalculator show={isCalc} onClose={() => setIsCalc(false)} />
    </div>
  );
};
export default QuestionContent;
